import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card, CardContent } from "@/platform/ui/card";
import { useSession } from "@/platform/auth/use-session";
import { useProfile } from "@/platform/auth/use-profile";
import {
  computeSummary,
  endDiagnosisSession,
  recordAttempt,
  startDiagnosisSession,
} from "@/tenants/einstein/features/diagnosis/api";
import type { DiagnosisQuestion, DiagnosisSummary } from "@/tenants/einstein/features/diagnosis/types";
import { regenerateWeeklyPlan } from "@/tenants/einstein/features/study-plan/api";
import { cn } from "@/platform/lib/cn";

const CONFIDENCE_LABELS = ["Chute", "Pouca", "Média", "Alta", "Certeza"];

type Phase = "loading" | "answering" | "finishing" | "done";

interface AttemptInMemory {
  questionId: string;
  answer: string;
  timeSpentMs: number;
  confidence: number;
  isCorrect: boolean | null;
}

export function DiagnosisSessionPage() {
  const navigate = useNavigate();
  const { session: authSession } = useSession();
  const { profile } = useProfile();
  const [phase, setPhase] = useState<Phase>("loading");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<DiagnosisQuestion[]>([]);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<number>(2);
  const [attempts, setAttempts] = useState<AttemptInMemory[]>([]);
  const [summary, setSummary] = useState<DiagnosisSummary | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const questionStartRef = useRef<number>(Date.now());

  // Boot session uma única vez. Se falhar, reseta o ref para permitir nova
  // tentativa quando o usuário voltar (antes ficava travado em "loading" pra
  // sempre porque bootedRef nunca era resetado em erro).
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    if (!authSession) return;
    bootedRef.current = true;
    (async () => {
      try {
        const { session, questions: qs } = await startDiagnosisSession(authSession.user.id);
        if (qs.length === 0) {
          toast.error("Sem questões disponíveis para diagnóstico ainda.");
          navigate("/painel", { replace: true });
          return;
        }
        setSessionId(session.id);
        setQuestions(qs);
        startedAtRef.current = Date.now();
        questionStartRef.current = Date.now();
        setPhase("answering");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Não foi possível iniciar a sessão.");
        bootedRef.current = false;
        navigate("/painel", { replace: true });
      }
    })();
  }, [authSession, navigate]);

  const currentQuestion = questions[index];

  const handleAdvance = useCallback(async () => {
    if (!currentQuestion || !authSession || !sessionId) return;
    if (!selected) {
      toast.error("Escolha uma alternativa.");
      return;
    }
    const timeSpentMs = Date.now() - questionStartRef.current;
    const isCorrect = currentQuestion.correct_answer
      ? selected === currentQuestion.correct_answer
      : null;
    const attempt: AttemptInMemory = {
      questionId: currentQuestion.id,
      answer: selected,
      timeSpentMs,
      confidence,
      isCorrect,
    };
    setAttempts((prev) => [...prev, attempt]);
    try {
      await recordAttempt({
        userId: authSession.user.id,
        sessionId,
        questionId: currentQuestion.id,
        answer: selected,
        correctAnswer: currentQuestion.correct_answer,
        timeSpentMs,
        confidence,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao gravar tentativa.");
      // segue mesmo assim — não bloqueia o usuário
    }
    setSelected(null);
    setConfidence(2);
    if (index + 1 >= questions.length) {
      await finalize();
    } else {
      setIndex((i) => i + 1);
      questionStartRef.current = Date.now();
    }
  }, [authSession, confidence, currentQuestion, index, questions.length, selected, sessionId]);

  const finalize = useCallback(async () => {
    if (!sessionId) return;
    setPhase("finishing");
    try {
      const sm = await computeSummary(sessionId);
      await endDiagnosisSession(sessionId, {
        rawScore: sm.raw_score,
        durationSeconds: sm.duration_seconds,
        byDiscipline: sm.by_discipline,
      });
      // Atualiza plano semanal com os snapshots novos (silencioso; não bloqueia).
      if (authSession && profile) {
        try {
          await regenerateWeeklyPlan({
            userId: authSession.user.id,
            weeklyHours: profile.weekly_hours ?? 10,
            examDate: profile.exam_date,
          });
        } catch (err) {
          console.warn("[diagnosis] regen plan failed", err);
        }
      }
      setSummary(sm);
      setPhase("done");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao finalizar.");
      setPhase("done");
    }
  }, [sessionId]);

  if (phase === "loading") {
    return (
      <FullPageState
        icon={<Loader2 className="size-5 animate-spin" aria-hidden />}
        text="Montando seu diagnóstico…"
      />
    );
  }
  if (phase === "finishing") {
    return (
      <FullPageState
        icon={<Loader2 className="size-5 animate-spin" aria-hidden />}
        text="Calculando seu resultado…"
      />
    );
  }
  if (phase === "done" && summary) {
    return <SummaryView summary={summary} onContinue={() => navigate("/plano")} />;
  }

  if (!currentQuestion) return null;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Container size="default" className="relative py-12 md:py-16">
        <header className="flex items-center justify-between gap-4">
          <div>
            <Badge variant="accent">
              Diagnóstico · {index + 1} / {questions.length}
            </Badge>
            <h1 className="mt-3 font-display text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)]">
              {disciplineLabel(currentQuestion.discipline)}
            </h1>
          </div>
          <SessionTimer startedAt={startedAtRef.current} />
        </header>

        <div className="mt-4">
          <Progress current={index + 1} total={questions.length} />
        </div>

        <Card className="mt-8 p-7 md:p-8">
          <CardContent className="p-0 space-y-6">
            {currentQuestion.has_base_text && currentQuestion.base_text ? (
              <details className="rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5 text-[length:var(--text-body-sm)]">
                <summary className="cursor-pointer font-semibold text-[color:var(--color-foreground)]">
                  Texto-base · clique para ler
                </summary>
                <div className="mt-3 whitespace-pre-wrap text-[color:var(--color-muted-foreground)]">
                  {currentQuestion.base_text}
                </div>
              </details>
            ) : null}

            <div className="whitespace-pre-wrap text-[length:var(--text-body)] text-pretty leading-relaxed">
              {currentQuestion.stem}
            </div>

            <fieldset className="space-y-2.5">
              <legend className="sr-only">Alternativas</legend>
              {currentQuestion.alternatives.map((alt) => {
                const isSelected = selected === alt.label;
                return (
                  <label
                    key={alt.label}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-150 ease-out",
                      isSelected
                        ? "border-[--color-accent] bg-[color:var(--color-accent-soft)]"
                        : "border-[--color-border] hover:border-[--color-accent]/40 hover:bg-[color:var(--color-paper)]",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 grid size-6 place-items-center rounded-full text-[length:var(--text-caption)] font-bold shrink-0 transition-colors",
                        isSelected
                          ? "bg-[color:var(--color-accent)] text-white"
                          : "bg-[color:var(--color-muted)] text-[color:var(--color-muted-foreground)]",
                      )}
                    >
                      {alt.label}
                    </span>
                    <input
                      type="radio"
                      name="alternative"
                      value={alt.label}
                      checked={isSelected}
                      onChange={() => setSelected(alt.label)}
                      className="sr-only"
                    />
                    <span className="flex-1 text-[length:var(--text-body-sm)] leading-relaxed">
                      {alt.text}
                    </span>
                  </label>
                );
              })}
            </fieldset>

            <div className="space-y-3 rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5">
              <p className="text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Confiança
              </p>
              <div className="flex flex-wrap gap-2">
                {CONFIDENCE_LABELS.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setConfidence(i)}
                    className={cn(
                      "rounded-full border-2 px-4 py-1.5 text-[length:var(--text-caption)] font-medium transition-colors duration-150 ease-out active:scale-[0.97]",
                      confidence === i
                        ? "border-[--color-accent] bg-[color:var(--color-accent)] text-white shadow-[var(--shadow-accent)]"
                        : "border-[--color-border] bg-[color:var(--color-card)] text-[color:var(--color-muted-foreground)] hover:border-[--color-accent]/40 hover:text-[color:var(--color-foreground)]",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 flex items-center justify-between gap-4">
          <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            {attempts.length} respondidas · {questions.length - attempts.length} restantes
          </p>
          <Button size="xl" onClick={handleAdvance} disabled={!selected}>
            {index + 1 === questions.length ? "Finalizar diagnóstico" : "Próxima"}
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        </div>
      </Container>
    </div>
  );
}

function Progress({ current, total }: { current: number; total: number }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-[color:var(--color-border)]">
      <div
        className="h-full bg-[color:var(--color-foreground)] transition-all"
        style={{ width: `${pct}%` }}
        aria-hidden
      />
    </div>
  );
}

function SessionTimer({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.floor((now - startedAt) / 1000);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-4 py-2 font-mono text-[length:var(--text-body-sm)] font-semibold shadow-[var(--shadow-xs)]">
      <Clock className="size-3.5 text-[color:var(--color-accent)]" aria-hidden />
      <span data-tabular="true">
        {mm}:{ss}
      </span>
    </div>
  );
}

function FullPageState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-[color:var(--color-muted-foreground)]">
        {icon}
        <p>{text}</p>
      </div>
    </div>
  );
}

function SummaryView({
  summary,
  onContinue,
}: {
  summary: DiagnosisSummary;
  onContinue: () => void;
}) {
  const minutes = Math.floor(summary.duration_seconds / 60);
  const seconds = summary.duration_seconds % 60;
  const accuracy = summary.answered > 0 ? Math.round((summary.correct / summary.answered) * 100) : 0;
  const sortedDisc = useMemo(
    () =>
      Object.entries(summary.by_discipline).sort((a, b) => {
        const accA = a[1].total > 0 ? a[1].correct / a[1].total : 0;
        const accB = b[1].total > 0 ? b[1].correct / b[1].total : 0;
        return accA - accB;
      }),
    [summary.by_discipline],
  );

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Container size="default" className="relative py-14 md:py-20">
        <Badge variant="accentSolid">Diagnóstico concluído</Badge>
        <h1 className="mt-4 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
          <span data-tabular="true" className="text-gradient-accent">
            {summary.correct} de {summary.total}
          </span>{" "}
          acertos.
        </h1>
        <p className="mt-3 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
          Taxa de acerto {accuracy}% em {minutes} min {seconds}s. Esta é a foto inicial — a curva
          importa muito mais que a primeira amostra.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {sortedDisc.map(([disc, stats], i) => {
            const pct = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
            const isWeak = pct < 50;
            return (
              <div
                key={disc}
                className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)] animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center justify-between">
                  <p className="font-display text-[length:var(--text-h4)] font-semibold">
                    {disciplineLabel(disc)}
                  </p>
                  <Badge variant={isWeak ? "pink" : "mint"}>
                    {stats.correct}/{stats.total}
                  </Badge>
                </div>
                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${isWeak ? "bg-[color:var(--color-pink)]" : "bg-[color:var(--color-mint)]"}`}
                    style={{ width: `${pct}%` }}
                    aria-hidden
                  />
                </div>
                <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  {pct}% de acerto
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10">
          <Button size="xl" variant="gradient" onClick={onContinue}>
            Ver meu plano da semana
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        </div>

        <p className="mt-6 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          Em breve: nota padronizada estimada, plano semanal calibrado pelos seus gargalos.
        </p>
      </Container>
    </div>
  );
}

const DISCIPLINE_LABELS: Record<string, string> = {
  portugues: "Português",
  ingles: "Inglês",
  matematica: "Matemática",
  biologia: "Biologia",
  quimica: "Química",
  fisica: "Física",
  historia: "História",
  geografia: "Geografia",
};

function disciplineLabel(slug: string): string {
  return DISCIPLINE_LABELS[slug] ?? slug;
}
